import type { AgentConfiguratorState } from '../types/agentConfigurator';

/**
 * Merges the last full config JSON (from API or import) with the typed state produced by the
 * Integration Configurator UI, so keys the UI does not edit (e.g. compact_context_enabled,
 * coordinator.use_user_message_injection_tool, extra metaPrompts) survive Save.
 *
 * - Top-level keys only present on the server stay on the object.
 * - coordinator / metaPrompts: shallow merge (UI wins on overlapping keys).
 * - workers: array length follows UI; each row is shallow-merged with the previous row at the same index.
 */
export function mergePreservedConnectorConfig(
  preserved: Record<string, unknown> | null | undefined,
  ui: AgentConfiguratorState
): Record<string, unknown> {
  const uiObj = JSON.parse(JSON.stringify(ui)) as Record<string, unknown>;

  if (!preserved || typeof preserved !== 'object') {
    return uiObj;
  }

  const base = JSON.parse(JSON.stringify(preserved)) as Record<string, unknown>;

  for (const k of ['approach', 'async_orchestrator', 'reminder_prompt_position'] as const) {
    if (uiObj[k] !== undefined) base[k] = uiObj[k];
  }

  const uCoord = (uiObj.coordinator || {}) as Record<string, unknown>;
  const bCoord =
    base.coordinator && typeof base.coordinator === 'object'
      ? { ...(base.coordinator as Record<string, unknown>) }
      : {};
  base.coordinator = { ...bCoord, ...uCoord };

  const uw = Array.isArray(uiObj.workers) ? (uiObj.workers as Record<string, unknown>[]) : [];
  const bw = Array.isArray(base.workers)
    ? ([...(base.workers as unknown[])] as Record<string, unknown>[])
    : [];
  base.workers = uw.map((w, i) => {
    const prev = bw[i] && typeof bw[i] === 'object' ? { ...bw[i] } : {};
    return { ...prev, ...w };
  });

  const uMeta = (uiObj.metaPrompts || {}) as Record<string, unknown>;
  const bMeta =
    base.metaPrompts && typeof base.metaPrompts === 'object'
      ? { ...(base.metaPrompts as Record<string, unknown>) }
      : {};
  base.metaPrompts = { ...bMeta, ...uMeta };

  return base;
}
